import moment from 'moment'
import { useUserStore } from '@/store/user'
import { useNavigationStore } from '@/store/navigation'
import CountryCodeList from '@/config/country-codes.json'
import passwordService from './password-service.js';
import { parse, differenceInYears } from 'date-fns'
import { Mask } from 'maska'

class UtilService {
  // mcRASE2 array order (per the AI team's spec): 0 Normal, 1 Diabetic retinopathy,
  // 2 Glaucoma / other optic disc alteration, 3 AMD / macular alteration,
  // 4 Nevus / hyperpigmented lesion, 5 Other retinal alterations. Same thresholds as
  // AIService (src/services/ai-service.js) so a class only counts as "detected" here if
  // it would also count as an AI badge match elsewhere in the app.
  MC_RASE2_DR_LOW_THRESHOLD = 0.004
  MC_RASE2_OTHER_CLASSES_LOW_THRESHOLD = 0.2
  MC_RASE2_CLASS_KEYS = {
    1: 'diabeticRetinopathy',
    2: 'opticNerveAlterations',
    3: 'macularAlterations',
    4: 'pigmentaryAlterations',
    5: 'otherAlterations',
  }

  // Used by getMcRASE2FindingKeys below — parses the `metadata` JSON string down to the
  // raw mcRASE2 score array, or null when there's nothing usable (missing metadata,
  // unparseable, or an unrecognized version).
  //
  // Two different real payloads confirmed the version marker lands in two different spots
  // depending on which pipeline run wrote the capture: newer ones nest it at
  // aiMetadata.mcRASE2.version ("1.0"), older ones carry it as a sibling of mcRASE2 —
  // aiMetadata.modelVersion ("mcRASE2_1.0"), the legacy shape also used by the EXAM's own
  // separate mcRASE2OD/mcRASE2OS metadata for the grid badge. Both shapes coexist in
  // production (checking only one meant every capture using the OTHER shape always came
  // back null, findings or not) — accept either.
  parseMcRASE2Scores(metadataString) {
    if (!metadataString)
      return null

    let metadata
    try {
      metadata = JSON.parse(metadataString)
    } catch (err) {
      return null
    }

    const aiMetadata = metadata.aiMetadata
    const wrapper = aiMetadata?.mcRASE2
    const mcRASE2 = wrapper?.mcRASE2
    if (!mcRASE2)
      return null

    const isRecognizedVersion = wrapper.version === '1.0' || aiMetadata.modelVersion === 'mcRASE2_1.0'
    if (!isRecognizedVersion)
      return null

    return mcRASE2
  }

  /**
   * Reads a single capture's AI classification — the `metadata` JSON string of an
   * ExamData COLOR/HEATMAP record, under aiMetadata.mcRASE2.mcRASE2 — and returns which
   * alteration class keys (src/config/locales/*.json, aiAssistant.findings.*) apply to it.
   * @param {string} metadataString the raw `metadata` field of an ExamData record
   * @returns {string[]|null} null when there's no usable AI reading at all (missing
   *          metadata, unparseable, an unrecognized modelVersion, or — when no class 1-5
   *          clears its threshold — a missing/zero index 0 too, see below) — the caller
   *          should fall back to something else in that case. Otherwise an array: 0 keys =
   *          normal (AI ran and found nothing), 1 key = single finding, 2 keys = a
   *          combination. When more than 2 classes clear the threshold, only the 2
   *          highest-scoring are kept, since neither the AI Assistant tab nor the model
   *          combines more than two classes on the same image.
   */
  getMcRASE2FindingKeys(metadataString) {
    const mcRASE2 = this.parseMcRASE2Scores(metadataString)
    if (!mcRASE2)
      return null

    const detected = []
    for (let classIndex = 1; classIndex < mcRASE2.length; classIndex++) {
      const score = mcRASE2[classIndex]
      const threshold = classIndex === 1 ? this.MC_RASE2_DR_LOW_THRESHOLD : this.MC_RASE2_OTHER_CLASSES_LOW_THRESHOLD
      if (score > threshold)
        detected.push({ classIndex, score })
    }

    // Mirrors AIService.processMCRASE2Score's own klass===0 handling (ExamImageTab.js,
    // the multiclass fallback used when no class 1-5 clears its threshold): "normal" isn't
    // just the absence of a detected class, index 0 itself needs a real reading too. A
    // literal 0/missing/non-numeric index 0 means the AI didn't produce a usable score for
    // this capture at all — that's "no reading", not a confident "no alteration" — so it
    // falls back like any other unusable metadata instead of defaulting to normal.
    if (detected.length === 0) {
      const normalScore = mcRASE2[0]
      if (normalScore === undefined || typeof normalScore !== 'number' || normalScore === 0)
        return null
    }

    return detected
      .sort((a, b) => b.score - a.score)
      .slice(0, 2)
      .map(({ classIndex }) => this.MC_RASE2_CLASS_KEYS[classIndex])
  }


  b64_to_utf8(str) {
    return decodeURIComponent(escape(window.atob( str )));
  }

  utf8_to_b64(str) {
    return window.btoa(unescape(encodeURIComponent(str)));
  }

  dataQuery(json) {
    return this.utf8_to_b64(JSON.stringify(json))
  }

  parseDataQuery(queryData) {
    try {
      return JSON.parse(this.b64_to_utf8(queryData))
    } catch(err) {
      console.error("Error parsing URL query...")
      return {}
    }
  }

  formatDate(self, date) {
    if (date)
      return moment(date).format(self.$i18n.t('dateFormat'))

    return;
  }

  formatDateISO(date) {
    if (date)
      return moment(date).format('YYYY-MM-DD')

    return;
  }

  getPreviousRouteTitle(self) {
    const navigationStore = useNavigationStore()
    const previousRouteName = navigationStore.previousRouteName

    if (!previousRouteName)
      return null

    const previousRoute = self.$router.getRoutes().find(route => route.name === previousRouteName)
    const titleKey = previousRoute?.meta?.title

    return titleKey ? self.$i18n.t(titleKey) : null
  }

  getSectionEntryRouteTitle(self) {
    const navigationStore = useNavigationStore()
    const sectionEntryRouteName = navigationStore.sectionEntryRouteName

    if (!sectionEntryRouteName)
      return null

    const sectionEntryRoute = self.$router.getRoutes().find(route => route.name === sectionEntryRouteName)
    const titleKey = sectionEntryRoute?.meta?.title

    return titleKey ? self.$i18n.t(titleKey) : null
  }

  goToCustomRoute(target) {
    window.location =`/ec2/?_target-route=${target}`
  }

  uuidv4() {
    return ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
      (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
    );
  }

  raw(stuff) {
    return JSON.parse(JSON.stringify(stuff))
  }

  countryCodeList(locale) {
    let _locale = locale
    if (_locale.includes('-'))
      _locale = _locale.split('-')[0]

    switch(_locale) {
      case "pt":
        return ["AF","ZA","AL","DE","AD","AO","AI","AQ","AG","SA","DZ","AR","AM","AW","AU","AT","AZ","BS","BH","BD","BB","BE","BZ","BJ","BM","BY","BO","BQ","BA","BW","BR","BN","BG","BF","BI","BT","CV","CM","KH","CA","KZ","TD","CL","CN","CY","CO","KM","KR","KP","CI","CR","HR","CU","CW","DK","DJ","DM","EG","SV","AE","EC","ER","SK","SI","ES","US","EE","ET","FJ","PH","FI","FR","GA","GM","GH","GE","GI","GR","GD","GL","GP","GU","GT","GG","GF","GY","GQ","GW","GN","HT","HN","HK","HU","YE","BV","CX","IM","HM","NF","AX","KY","CC","CK","FO","GS","MH","UM","SB","VI","VG","IN","ID","IR","IQ","IE","IS","IL","IT","JM","JP","JE","JO","KI","KW","LA","LS","LV","LB","LR","LY","LI","LT","LU","MO","MG","MY","MW","MV","ML","MT","FK","MP","MA","MQ","MU","MR","YT","MX","FM","MZ","MD","MC","MN","ME","MS","MM","NA","NR","NP","NI","NE","NG","NU","NO","NC","NZ","OM","NL","PW","PS","PA","PG","PK","PY","PE","PN","PF","PL","PR","PT","QA","KE","KG","GB","CF","CZ","MK","CD","CG","DO","RE","RO","RW","RU","EH","PM","WS","AS","SM","SH","LC","BL","KN","MF","SX","ST","VC","SN","SL","RS","SC","SG","SY","SO","LK","SZ","SS","SD","SE","CH","SR","SJ","TH","TW","TJ","TZ","TF","IO","TL","TG","TO","TK","TT","TN","TC","TM","TR","TV","UA","UG","UY","UZ","VU","VA","VE","VN","WF","ZM","ZW"]
      case "es":
        return ["AF","AL","DE","AD","AO","AI","AQ","AG","SA","DZ","AR","AM","AW","AU","AT","AZ","BS","BD","BB","BH","BE","BZ","BJ","BM","BY","BO","BQ","BA","BW","BR","BN","BG","BF","BI","BT","CV","KH","CM","CA","QA","TD","CL","CN","CY","CO","KM","CG","KR","KP","CR","CI","HR","CU","CW","DK","DM","EC","EG","SV","AE","ER","SK","SI","ES","US","EE","ET","PH","FI","FJ","FR","GA","GM","GS","GE","GH","GI","GD","GR","GL","GP","GU","GT","GF","GG","GQ","GW","GN","GY","HT","HN","HK","HU","IN","ID","IQ","IR","IE","BV","IM","CX","HM","NF","IS","AX","KY","CC","CK","UM","FO","FK","MP","MH","SB","TC","VG","VI","IL","IT","JM","JP","JE","JO","KZ","KE","KG","KI","KW","LA","LS","LV","LB","LR","LY","LI","LT","LU","MO","MK","MG","MY","MW","MV","ML","MT","MA","MQ","MU","MR","YT","MX","FM","MD","MC","MN","ME","MS","MZ","MM","NA","NR","NP","NI","NE","NG","NU","NO","NC","NZ","OM","NL","PK","PW","PS","PA","PG","PY","PE","PN","PF","PL","PT","PR","GB","CF","CZ","CD","DO","RE","RW","RO","RU","EH","AS","WS","BL","KN","SM","MF","PM","VC","SH","LC","VA","ST","SN","RS","SC","SL","SG","SX","SY","SO","LK","SZ","ZA","SD","SS","SE","CH","SR","SJ","TH","TW","TZ","TJ","IO","TF","TL","TG","TK","TO","TT","TN","TM","TR","TV","UA","UG","UY","UZ","VU","VE","VN","WF","YE","DJ","ZM","ZW"]
      default: // # en
        return ["AF","AX","AL","DZ","AS","AD","AO","AI","AQ","AG","AR","AM","AW","AU","AT","AZ","BS","BH","BD","BB","BY","BE","BZ","BJ","BM","BT","BO","BQ","BA","BW","BV","BR","IO","BN","BG","BF","BI","CV","KH","CM","CA","KY","CF","TD","CL","CN","CX","CC","CO","KM","CG","CK","CR","CI","HR","CU","CW","CY","CZ","CD","DK","DJ","DM","DO","EC","EG","SV","GQ","ER","EE","ET","FK","FO","FJ","FI","FR","GF","PF","TF","GA","GM","GE","DE","GH","GI","GR","GL","GD","GP","GU","GT","GG","GW","GN","GY","HT","HM","VA","HN","HK","HU","IS","IN","ID","IR","IQ","IE","IM","IL","IT","JM","JP","JE","JO","KZ","KE","KI","KP","KR","KW","KG","LA","LV","LB","LS","LR","LY","LI","LT","LU","MO","MK","MG","MW","MY","MV","ML","MT","MH","MQ","MR","MU","YT","MX","FM","MD","MC","MN","ME","MS","MA","MZ","MM","NA","NR","NP","NL","NC","NZ","NI","NE","NG","NU","NF","MP","NO","OM","PK","PW","PS","PA","PG","PY","PE","PH","PN","PL","PT","PR","QA","RE","RO","RU","RW","BL","SH","KN","LC","MF","PM","VC","WS","SM","ST","SA","SN","RS","SC","SL","SG","SX","SK","SI","SB","SO","ZA","GS","SS","ES","LK","SD","SR","SJ","SZ","SE","CH","SY","TW","TJ","TZ","TH","TL","TG","TK","TO","TT","TN","TR","TM","TC","TV","UG","UA","AE","GB","UM","US","UY","UZ","VU","VE","VN","VG","VI","WF","EH","YE","ZM","ZW"]
    }
  }

  isFeatureEnabled(feature) {
    const userStore = useUserStore()
    const session = userStore.sessionInfo
    const features = session.features
    return features.includes(feature)
  }


  canUseAiAssistant(session) {
    return !!session?.features?.includes('aiAssistant') && session.currentClinic?.country === 'BR'
  }

  datePickerLocale() {
    const userStore = useUserStore()
    const session = userStore.sessionInfo

    const locale = session.currentClinic ? session.currentClinic.locale : this.getCurrentLanguage()

    if (locale == 'pt-br') {
      return 'pt-BR'
    } else if (locale == 'es') {
      return 'es'
    } else {
      return 'en-US'
    }
  }

  getCountryDdi(locale) {
    let result

    CountryCodeList.forEach(o => {
      if (o.code == locale)
        result = o.dial_code
    })

    return result
  }

  async validatePassword(account){
    try{
      let response =  await passwordService.zxcvbnValidation({account})

      if (response) {
        // console.log('zxcvbn Score: ', response.data.score)
        return response.data
      }
    } catch (err) {
      console.log('error: ', err)
    }
    return false
  }

  getCurrentLanguage() {
    let language = window.navigator.userLanguage || window.navigator.language
    if (language == 'pt' || language.startsWith('pt-')) {
      return 'pt-br'
    } else if (language.startsWith('en-')) {
      return 'en'
    } else if (language.startsWith('es-')) {
      return 'es'
    }

    return language
  }

  getIsPaidService() {
    let hasPaidService = false
    try{
        hasPaidService = this.isFeatureEnabled('paidService')
    } catch (error){
        console.log('error = ', error)
    }
    return hasPaidService
  }

  // unifiedReport = the clinic has access to the unified report (can switch experiences);
  // unifiedReportExperience = the clinic chose the unified experience. The unified experience
  // only counts while the clinic still has access, so removing unifiedReport sends it back to
  // the old report instead of stranding it on the new one with no way to switch back.
  canSwitchReportExperience(features) {
    return (features || []).includes('unifiedReport')
  }

  isUnifiedReportExperience(features) {
    return this.canSwitchReportExperience(features) && (features || []).includes('unifiedReportExperience')
  }

  getIsTempPaidFlag() {
    let hasTempPaidFlag = false
    try {
      hasTempPaidFlag = this.isFeatureEnabled('tempPaidFlag')
    } catch (error) {
      console.log('error getIsTempPaidFlag', error);
    }
    return hasTempPaidFlag
  }

  clearUrlFilters(self) {
    const currentPath = self.$route.path;
    self.$router.replace({ path: currentPath, query: {} });
  }

  getFormattedPatientDocumentWithSearchMatch(item, searchValue, i18nInstance) {
    if (!item)
      return

    const _normalizeString = (value) => {
      return value ? String(value).trim().toLowerCase() : ''
    }

    const _formatDocumentEntry = (labelKey, value, i18nInstance) => {
      return `${i18nInstance.t(labelKey)}: ${value}`
    }

    const _getPatientDocumentEntries = (patientItem) => {
      const entries = []
      const keys = Object.keys(patientItem)

      const documentKeys = keys.filter(key => key.startsWith('document') && /document\d+/.test(key));
      documentKeys.sort((a, b) => {
        const numberA = parseInt(a.replace('document', ''), 10)
        const numberB = parseInt(b.replace('document', ''), 10)
        return numberA - numberB
      })

      for (const docKey of documentKeys) {
        if (patientItem[docKey] && String(patientItem[docKey]).trim() !== '')
          entries.push({ key: docKey, value: patientItem[docKey] })
      }

      return entries
    }

    const documentEntries = _getPatientDocumentEntries(item)
    const normalizedSearchValue = _normalizeString(searchValue)
    const mrnValue = _normalizeString(item.mrn)

    if (normalizedSearchValue !== '') {
      if (mrnValue === normalizedSearchValue)
        return _formatDocumentEntry('mrn', item.mrn, i18nInstance)

      for (const docEntry of documentEntries) {
        if (_normalizeString(docEntry.value) === normalizedSearchValue)
          return _formatDocumentEntry(docEntry.key, docEntry.value, i18nInstance)
      }
    }

    if (mrnValue !== '')
      return _formatDocumentEntry('mrn', item.mrn, i18nInstance)

    if (documentEntries.length > 0) {
      const firstFilledDoc = documentEntries[0];
      return _formatDocumentEntry(firstFilledDoc.key, firstFilledDoc.value, i18nInstance);
    }

    return 'ID: '
  }

  getCountryCodeFromBrowserLocale() {
    const browserLocale = navigator.language || 'pt-BR'
    const separator = browserLocale.includes('-') ? '-' : (browserLocale.includes('_') ? '_' : null)

    if (separator) {
      const parts = browserLocale.split(separator)

      if (parts[1] && parts[1].length === 2)
        return parts[1].toUpperCase()
    }

    const lang = browserLocale.substring(0, 2).toLowerCase()
    const fallbackMap = {'pt': 'BR', 'es': 'ES', 'en': 'US'}

    return fallbackMap[lang] || null
  }

  getBannerImagePath(langCode, folder = 'phelcom', prefix = 'register_banner') {
    const supportedLocales = ['pt-br', 'en', 'es'];
    const lang = langCode ? langCode.toLowerCase() : 'en';
    const finalCode = supportedLocales.includes(lang) ? lang : 'en';

    try {
      return require(`@/assets/images/${folder}/${prefix}_${finalCode}.png`); // eslint-disable-line no-undef
    } catch (err) {
      return require(`@/assets/images/phelcom/register_banner_en.png`); // eslint-disable-line no-undef
    }
  }

  checkIfAdult(self, dateStr) {
    // Date provided by the user and check that it is not null
    if (!dateStr || dateStr.length < 8) return false;

    let dateFormat = self.$i18n.t('dateFormatFns');
    try {
      const birthDate = parse(dateStr, dateFormat, new Date());
      const age = differenceInYears(new Date(), birthDate);
      return age >= 18;
    } catch (error) {
      console.log('error', error);
      return false;
    }
  }

  getAllowedExtensions() {
    return [
      'pdf', 'doc', 'docx', 'txt',
      'png', 'jpg', 'jpeg', 'dcm', 'dicom', 'webp', 'gif',
      'mp4', 'mkv', 'avi', 'mpeg', 'mpg'
    ];
  }

  getAcceptAttribute() {
    return this.getAllowedExtensions().map(ext => `.${ext}`).join(',');
  }

  getFormattedAllowedExtensions() {
    const extensions = this.getAllowedExtensions();
    return extensions
      .map(ext => ext.toUpperCase())
      .join(', ');
  }

  getSupportedGalleryVideos() {
    return ['mp4', 'webm', 'ogg'];
  }

  getSupportedGalleryImages() {
    return ['png', 'jpg', 'jpeg', 'webp', 'gif', 'svg', 'bmp'];
  }

  isSupportedGalleryVideo(extension) {
    if (!extension) return false;
    return this.getSupportedGalleryVideos().includes(extension.toLowerCase());
  }

  isSupportedGalleryImage(extension) {
    if (!extension) return false;
    return this.getSupportedGalleryImages().includes(extension.toLowerCase());
  }

  getFileIcon(type) {
    if (!type) return 'fa fa-file-o';
    if (type.includes('PDF')) return 'fa fa-file-pdf-o';
    if (type.includes('DOC')) return 'fa fa-file-word-o';
    if (type.includes('TXT')) return 'fa fa-file-text-o';
    if (type.includes('VIDEO')) return 'fa fa-file-video-o';
    if (type.includes('IMAGE')) return 'fa fa-file-image-o';
    return 'fa fa-file-o';
  }

  triggerFileDownload(url) {
    const el = document.createElement('a')
    el.href = url
    document.body.appendChild(el)
    el.click()
    document.body.removeChild(el)
  }

  async downloadBlobFromUrl(url, filename) {
    const res = await fetch(url, {
      method: 'GET',
      cache: 'no-store'
    })
    const blob = await res.blob()
    const objectURL = URL.createObjectURL(blob)
    const el = document.createElement('a')
    el.href = objectURL
    el.download = filename
    document.body.appendChild(el)
    el.click()
    document.body.removeChild(el)
    URL.revokeObjectURL(objectURL)
  }

  startHoverPreview(vm, key, delay = 1200) {
    clearTimeout(vm.hoverVideoTimer)
    vm.hoverVideoTimer = setTimeout(() => {
      vm.playingVideoIndex = key
    }, delay)
  }

  cancelHoverPreview(vm, key) {
    clearTimeout(vm.hoverVideoTimer)
    vm.hoverVideoTimer = null
    if (vm.playingVideoIndex === key) {
      vm.playingVideoIndex = null
    }
  }

  playHoverVideo(el) {
    if (!el) return
    const playPromise = el.play()
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {})
    }
  }

  lgInjectLateralityBox(onSwitchCallback, self) {
    if (document.querySelector('.lg-laterality-box'))
      return

    const toolbar = document.querySelector('.lg-toolbar')

    if (!toolbar)
      return

    const box = document.createElement('div')
    box.className = 'lg-laterality-box'
    box.innerHTML = `
      <div class="lg-lat-toggle">
        <button class="lg-lat-btn" data-lat="L">${self.$i18n.t('oe')}</button>
        <button class="lg-lat-btn" data-lat="R">${self.$i18n.t('od')}</button>
      </div>
    `
    toolbar.appendChild(box)

    box.querySelectorAll('.lg-lat-btn').forEach(btn => {
      btn.addEventListener('click', (e) => onSwitchCallback(e.currentTarget.dataset.lat))
    })
  }

  lgUpdateLateralityDisplay(laterality) {
    const box = document.querySelector('.lg-laterality-box')
    if (!box) return

    box.classList.toggle('lg-lat-no-selection', !laterality)
    box.querySelectorAll('.lg-lat-btn').forEach(btn => {
      btn.classList.toggle('active', btn.dataset.lat === laterality)
    })
  }

  lgClose() {
    document.querySelector('.lg-laterality-box')?.remove()
  }

  /**
   * Applies visual mask patterns to specific fields of a data object.
   * Useful for formatting raw backend data (e.g., "1234") into UI-friendly 
   * formats (e.g., "123.4") before rendering or setting initial states.
   *
   * @param {Object} i18n - The Vue-i18n instance to translate mask keys.
   * @param {Object} data - The raw data object containing the values to be masked.
   * @param {Object} maskMap - A dictionary mapping the object keys to their respective i18n mask keys (e.g., { document2: 'inputMask.document2' }).
   * @returns {Object} A new data object with the targeted fields properly masked.
   */
  applyFieldMasks(i18n, data, maskMap) {
    if (!data || !maskMap) return data;

    const formattedData = { ...data };

    for (const field in maskMap) {
      const originalValue = formattedData[field];

      if (originalValue) {
        const maskTranslation = i18n.t(maskMap[field]);
        const maskInstance = new Mask({ mask: maskTranslation });
        
        formattedData[field] = maskInstance.masked(String(originalValue));
      }
    }

    return formattedData;
  }

  // A thumbnail's <img> fires @error when its source 404s/fails to load (deleted file,
  // still-processing image, broken S3 key, ...) — the .loading-eye shimmer would otherwise
  // spin forever since @load, the only thing that clears it, never fires. Swaps the shimmer
  // for the .image-failed state instead and turns the whole thumbnail into a fallback
  // download trigger, so there's still a way to get at the file even when the preview can't
  // render it. doDownloadFn is passed in (not called via `this`) so this one function works
  // for every component's own doDownload, whatever its signature/context.
  //
  // visibility (not display:none): .thumbnail-box itself carries no size of its own — its
  // height comes entirely from this <img>'s aspect-ratio (see _general.scss/each tab's own
  // thumbnail-box rules), and .loading-eye's min-height, which papered over that while
  // loading, just got removed above. display:none would drop the img out of layout
  // entirely and collapse the container to 0×0, leaving .image-failed's background
  // rendering into nothing visible — confirmed against a real broken thumbnail, and the
  // same gap exists upstream (this pairing is unchanged from develop). visibility:hidden
  // keeps the img's box (and so the container's size) intact while still hiding the
  // browser's broken-image icon/alt text.
  // LightGallery binds its own click listener directly on every .thumbnail-box at gallery
  // init (openGalleryOnItemClick in lightgallery/lib/lightgallery.js — one addEventListener
  // per item, not delegated), which happens well before any image could possibly have
  // failed to load yet. A listener added here, on the same element, after the fact, always
  // fires SECOND — same-element listeners run in registration order regardless of
  // stopPropagation/preventDefault called from the later one, so LightGallery's own
  // openGallery() had already run by the time either of those would fire. A single
  // capture-phase listener on document intercepts the click on its way DOWN to the target,
  // before LightGallery's target-level listener is ever reached, so it never runs at all.
  _registerFailedThumbnailDownloadInterceptor() {
    if (this._failedThumbnailInterceptorRegistered) return
    this._failedThumbnailInterceptorRegistered = true

    document.addEventListener('click', (event) => {
      const container = event.target.closest('.thumbnail-box.image-failed')
      if (!container?._pendingDownload) return
      event.preventDefault()
      event.stopPropagation()
      container._pendingDownload()
    }, true)
  }

  // Generic helper for any vee-validate form split across scrollable sections (ClinicFormData,
  // and any future form with the same layout): `errors` is the map vee-validate's
  // handleSubmit(onValid, onInvalid) hands back, keyed by field name in registration/DOM
  // order. Without this, a required field failing validation in a section that isn't the one
  // currently scrolled into view gives no visible feedback at all - the inline error message
  // next to the field is there, just off-screen - so clicking submit silently does nothing.
  // Relies on each field's wrapper/input sharing its vee-validate `name` as its DOM `id`,
  // the convention already used across these forms.
  scrollToFirstInvalidField(errors) {
    const firstInvalidField = Object.keys(errors || {})[0]
    if (!firstInvalidField)
      return

    const element = document.getElementById(firstInvalidField)
    if (!element)
      return

    element.scrollIntoView({ behavior: 'smooth', block: 'center' })

    if (typeof element.focus === 'function')
      element.focus({ preventScroll: true })
  }

  enableDownloadOnError(event, item, doDownloadFn) {
    event.target.style.visibility = 'hidden'
    const container = event.target.closest('.thumbnail-box')
    if (!container) return
    container.classList.remove('loading-eye')
    container.classList.add('image-failed')
    container._pendingDownload = () => doDownloadFn(item, item.type.endsWith('VIDEO'))
    this._registerFailedThumbnailDownloadInterceptor()
  }

  // FFA (angiography) images carry faElapsedTime in their metadata: milliseconds between
  // the contrast injection and this capture. Formula from Paulo (leading the new Eyer
  // mobile app, source of this field).
  formatTimeFA(ms) {
    const totalSeconds = Math.floor(ms / 1000)
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60

    return String(minutes).padStart(2, '0') + ':' + String(seconds).padStart(2, '0')
  }

  getImageFaElapsedTime(image) {
    if (!image?.metadata) return null

    let metadata
    try {
      metadata = JSON.parse(image.metadata)
    } catch {
      return null
    }

    if (typeof metadata.faElapsedTime !== 'number') return null
    return this.formatTimeFA(metadata.faElapsedTime)
  }

  // Types whose image-badge (ExamGridList.vue/ExamImageTab.vue) shows the shared AI-status
  // dot + a type label instead of the dedicated COLOR/"AI" pill (that one stays tied to
  // eyerMaps until it's validated separately). FAF_BLUE was dropped from the Eyer 2 Pro
  // scope — only the green variant ships.
  TYPE_STATUS_BADGE_TYPES = ['FFA', 'RNFL', 'FAF_GREEN']

  hasTypeStatusBadge(type) {
    return this.TYPE_STATUS_BADGE_TYPES.includes(type)
  }

  // Mirrors the Eyer mobile app's own getTypeLabel(type) so the badge reads the same on
  // both apps — 'FA' is mobile's legacy alias for FFA, kept here for parity even though
  // EyerCloud data never uses it. All-uppercase (EyerCloud convention); types with no named
  // mapping fall back to their first underscore-separated segment (STEREO_IMAGE,
  // PROGRESSION_VIDEO... show only STEREO, PROGRESSION), not the full compound string.
  // `self` is the calling component, needed for the ANTERIOR i18n lookup (same convention
  // as formatDate above).
  badgeTypeLabel(self, type) {
    switch (type) {
      case 'ANTERIOR':
        return self.$i18n.t('captureItemExamDataLabel.anterior').toUpperCase()
      case 'FAF_GREEN':
        return 'AUTOFLUOR'
      case 'COLOR':
        return 'RETINA'
      case 'FFA':
      case 'FA':
        return 'ANGIO'
      case 'REDFREE':
        return 'RF'
      case 'RETINA_INFRA_RED':
        return 'IR'
      case 'RNFL_REDFREE':
        return 'REDFREE PRO'
      case 'RNFL_PANO':
        return 'COLOR PRO'
      default:
        return type.split('_')[0].toUpperCase()
    }
  }

  goToLink(self, active, link) {
    self.active = active

    self.$router.push({name: 'proxy-router', params: {
      target: link,
      data: JSON.stringify({})
    }})
  }
}

const utilService = new UtilService()
export default utilService
